import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Check,
  Sparkles,
  Zap,
  Crown,
  ArrowRight,
  Shield,
  TrendingUp,
  Calculator,
  FileText,
  Bell,
  Gauge,
  Receipt,
  Bot,
  FolderOpen,
  BarChart3,
  CreditCard,
  Users,
  Gavel,
  DollarSign,
  Star,
  ChevronDown,
  HelpCircle,
  MessageCircle
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const PlanosPage = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [expandedFeature, setExpandedFeature] = useState(null);
  const [hoveredPlan, setHoveredPlan] = useState(null);

  const navigateTo = (url) => {
    window.location.href = url;
  };

  const plans = [
    {
      id: 'basico',
      name: 'Plano Básico',
      icon: Zap,
      price: 89.90,
      yearlyPrice: 79.90,
      description: 'Ideal para quem está começando e quer organizar seu MEI',
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      popular: false,
      features: [
        {
          icon: Calculator,
          title: 'Gerenciamento Financeiro',
          description: 'Registro de entradas e saídas categorizadas, controle de contas a pagar e receber com status, visualização de saldo e projeção.',
        },
        {
          icon: FolderOpen,
          title: 'Central de Documentos',
          description: 'Espaço seguro para organizar cartão CNPJ, documentos pessoais e comprovantes. Acesso rápido no celular e computador.',
        },
        {
          icon: BarChart3,
          title: 'Relatório Anual',
          description: 'Relatório completo com resumo de faturamento, consumo do limite de R$ 81 mil e demonstrativo de despesas.',
        },
        {
          icon: Shield,
          title: 'Piloto Automático MEI',
          description: 'Geração automática da DAS mensal, monitoramento de irregularidades e dívidas ativas. CNPJ 100% regular.',
        },
        {
          icon: Bell,
          title: 'Lembretes de Pagamento',
          description: 'Lembretes para todas as contas e obrigações fiscais que você precisa pagar.',
        },
        {
          icon: Gauge,
          title: 'Limite Guardião',
          description: 'Acompanhamento em tempo real das emissões de NF para não estourar o limite de R$ 81 mil/ano.',
        },
        {
          icon: Receipt,
          title: 'Emissão de Notas Fiscais',
          description: '10 notas/mês inclusas. Notas excedentes por apenas R$ 0,25 cada.',
          highlight: '10 notas/mês',
        },
        {
          icon: Bot,
          title: 'Assistente de IA',
          description: 'IA interativa para dicas financeiras, dúvidas sobre MEI, orientação sobre notas e suporte da plataforma.',
        },
      ],
    },
    {
      id: 'pro',
      name: 'Plano Pro',
      icon: Crown,
      price: 179.90,
      yearlyPrice: 159.90,
      description: 'Para MEIs que querem crescer e vender mais',
      color: '#8b5cf6',
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      popular: true,
      includesBasic: true,
      features: [
        {
          icon: Receipt,
          title: 'Emissão de Notas Fiscais',
          description: '30 notas/mês inclusas. Notas excedentes por apenas R$ 0,20 cada.',
          highlight: '30 notas/mês',
        },
        {
          icon: DollarSign,
          title: 'Meu Preço',
          description: 'Ferramenta inteligente que calcula o preço ideal considerando custos, tempo de produção e margem de lucro.',
          isNew: true,
        },
        {
          icon: CreditCard,
          title: 'Crédito Expresso',
          description: 'Sistema integrado que conecta você às melhores oportunidades de crédito, capital de giro e microcrédito.',
          isNew: true,
        },
        {
          icon: Users,
          title: 'CRM MEI',
          description: 'Organize contatos, cadastre clientes, acompanhe negociações e gerencie vendas do início ao fim.',
          isNew: true,
        },
        {
          icon: Gavel,
          title: 'Licita AI',
          description: 'IA especializada em licitações que explica regras, requisitos e orienta como participar de processos públicos.',
          isNew: true,
        },
      ],
    },
  ];

  const faqs = [
    {
      question: 'Posso trocar de plano depois?',
      answer: 'Sim! Você pode fazer upgrade ou downgrade do seu plano a qualquer momento. A diferença será calculada proporcionalmente.',
    },
    {
      question: 'Como funciona o período de teste?',
      answer: 'Oferecemos 7 dias grátis para você testar todas as funcionalidades do plano escolhido. Sem compromisso!',
    },
    {
      question: 'Quais formas de pagamento são aceitas?',
      answer: 'Aceitamos cartão de crédito, débito, PIX e boleto bancário. Pagamentos recorrentes apenas via cartão.',
    },
    {
      question: 'O que acontece se eu exceder o limite de notas?',
      answer: 'Você continua emitindo normalmente! Cada nota excedente é cobrada automaticamente no valor indicado no seu plano.',
    },
  ];

  const FeatureCard = ({ feature, index, planColor, planId }) => {
    const featureKey = `${planId}-${index}`;
    const isExpanded = expandedFeature === featureKey;

    return (
      <div className="relative">
        <div
          onClick={() => setExpandedFeature(isExpanded ? null : featureKey)}
          className={`flex items-start gap-3 p-4 rounded-xl cursor-pointer transition-all duration-300 ${
            isExpanded ? 'bg-gray-100' : 'hover:bg-gray-50'
          }`}
        >
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${planColor}15` }}
          >
            <feature.icon size={20} style={{ color: planColor }} />
          </div>
          <div className="flex-grow">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-semibold text-gray-800">{feature.title}</h4>
              {feature.isNew && (
                <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-purple-100 text-purple-600">
                  NOVO
                </span>
              )}
              {feature.highlight && (
                <span 
                  className="px-2 py-0.5 text-xs font-bold rounded-full text-white"
                  style={{ backgroundColor: planColor }}
                >
                  {feature.highlight}
                </span>
              )}
            </div>
            {isExpanded && (
              <p className="text-gray-600 text-sm mt-2 animate-fadeIn">
                {feature.description}
              </p>
            )}
          </div>
          <div
            className={`text-gray-400 flex-shrink-0 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
          >
            <ChevronDown size={20} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
          }}
        />

        {/* Elementos decorativos */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div
            className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-30 animate-spin-slow"
            style={{
              background: 'conic-gradient(from 0deg, #10b981, #8b5cf6, #10b981)',
              filter: 'blur(80px)',
              animationDuration: '50s',
            }}
          />
          <div
            className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full opacity-20 animate-spin-slow-reverse"
            style={{
              background: 'conic-gradient(from 180deg, #8b5cf6, #10b981, #8b5cf6)',
              filter: 'blur(60px)',
              animationDuration: '60s',
            }}
          />

          {/* Grid */}
          <div 
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `
                linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px',
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <Sparkles size={16} className="text-[#d9f99d] animate-pulse" />
              <span className="text-sm font-medium text-white">
                Escolha o plano ideal para você
              </span>
            </motion.div>

            {/* Título */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
            >
              Planos feitos para o{' '}
              <span
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #8b5cf6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                seu MEI crescer
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-xl text-gray-400 max-w-2xl mx-auto mb-10"
            >
              Ferramentas profissionais por um preço que cabe no bolso do microempreendedor
            </motion.p>

            {/* Toggle de período */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="inline-flex items-center gap-4 p-2 rounded-full bg-white/10 backdrop-blur-sm"
            >
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-3 rounded-full font-medium transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-white text-gray-900'
                    : 'text-white hover:text-gray-200'
                }`}
              >
                Mensal
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-6 py-3 rounded-full font-medium transition-all flex items-center gap-2 ${
                  billingCycle === 'yearly'
                    ? 'bg-white text-gray-900'
                    : 'text-white hover:text-gray-200'
                }`}
              >
                Anual
                <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-green-500 text-white">
                  -12%
                </span>
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Seção de Planos */}
      <section className="py-20 -mt-10 relative z-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, planIndex) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ delay: planIndex * 0.2, duration: 0.5 }}
                onMouseEnter={() => setHoveredPlan(plan.id)}
                onMouseLeave={() => setHoveredPlan(null)}
                className={`relative rounded-3xl overflow-hidden ${
                  plan.popular ? 'lg:-mt-4 lg:mb-4' : ''
                }`}
              >
                {/* Badge popular */}
                {plan.popular && (
                  <div 
                    className="absolute top-0 left-0 right-0 py-2 text-center text-white text-sm font-bold"
                    style={{ background: plan.gradient }}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Star size={16} className="fill-white" />
                      MAIS POPULAR
                      <Star size={16} className="fill-white" />
                    </div>
                  </div>
                )}

                <div 
                  className={`bg-white rounded-3xl p-8 h-full ${plan.popular ? 'pt-14' : ''}`}
                  style={{
                    boxShadow: hoveredPlan === plan.id
                      ? `0 25px 80px ${plan.color}30`
                      : '0 10px 40px rgba(0,0,0,0.1)',
                    border: plan.popular ? `2px solid ${plan.color}` : '2px solid transparent',
                  }}
                >
                  {/* Cabeçalho do plano */}
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ background: plan.gradient }}
                        >
                          <plan.icon size={24} className="text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                      </div>
                      <p className="text-gray-600">{plan.description}</p>
                    </div>
                  </div>

                  {/* Preço */}
                  <div className="mb-8">
                    <div className="flex items-end gap-2">
                      <span className="text-gray-500 text-lg">R$</span>
                      <motion.span
                        key={billingCycle}
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-5xl font-bold text-gray-900"
                      >
                        {billingCycle === 'monthly' 
                          ? plan.price.toFixed(2).replace('.', ',')
                          : plan.yearlyPrice.toFixed(2).replace('.', ',')
                        }
                      </motion.span>
                      <span className="text-gray-500 mb-2">/mês</span>
                    </div>
                    {billingCycle === 'yearly' && (
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-green-600 text-sm mt-1"
                      >
                        Economia de R$ {((plan.price - plan.yearlyPrice) * 12).toFixed(2).replace('.', ',')} por ano!
                      </motion.p>
                    )}
                  </div>

                  {/* CTA */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigateTo('https://app.meumei.net/register')}
                    className="w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 mb-8 transition-all"
                    style={{
                      background: plan.popular ? plan.gradient : 'transparent',
                      color: plan.popular ? 'white' : plan.color,
                      border: plan.popular ? 'none' : `2px solid ${plan.color}`,
                    }}
                  >
                    Começar agora
                    <ArrowRight size={20} />
                  </motion.button>

                  {/* Inclui plano básico */}
                  {plan.includesBasic && (
                    <div className="mb-6 p-4 rounded-xl bg-purple-50 border border-purple-100">
                      <p className="text-purple-700 font-medium flex items-center gap-2">
                        <Check size={18} />
                        Inclui todos os recursos do Plano Básico
                      </p>
                    </div>
                  )}

                  {/* Lista de recursos */}
                  <div className="space-y-1">
                    <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">
                      {plan.includesBasic ? 'Recursos exclusivos Pro' : 'Recursos inclusos'}
                    </h4>
                    {plan.features.map((feature, index) => (
                      <FeatureCard
                        key={`${plan.id}-${index}`}
                        feature={feature}
                        index={index}
                        planColor={plan.color}
                        planId={plan.id}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparativo */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Compare os planos
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Veja lado a lado o que cada plano oferece para escolher o melhor para você
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="overflow-x-auto"
          >
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b-2 border-gray-100">
                  <th className="text-left py-4 px-4 font-semibold text-gray-600">Recurso</th>
                  <th className="py-4 px-4">
                    <div className="text-center">
                      <span className="font-bold text-gray-900">Básico</span>
                      <p className="text-sm text-gray-500">R$ 89,90/mês</p>
                    </div>
                  </th>
                  <th className="py-4 px-4">
                    <div className="text-center">
                      <span className="font-bold text-purple-600">Pro</span>
                      <p className="text-sm text-gray-500">R$ 179,90/mês</p>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: 'Gerenciamento Financeiro', basico: true, pro: true },
                  { feature: 'Central de Documentos', basico: true, pro: true },
                  { feature: 'Relatório Anual', basico: true, pro: true },
                  { feature: 'Piloto Automático MEI', basico: true, pro: true },
                  { feature: 'Lembretes de Pagamento', basico: true, pro: true },
                  { feature: 'Limite Guardião', basico: true, pro: true },
                  { feature: 'Assistente de IA', basico: true, pro: true },
                  { feature: 'Notas Fiscais/mês', basico: '10', pro: '30' },
                  { feature: 'Nota excedente', basico: 'R$ 0,25', pro: 'R$ 0,20' },
                  { feature: 'Meu Preço', basico: false, pro: true },
                  { feature: 'Crédito Expresso', basico: false, pro: true },
                  { feature: 'CRM MEI', basico: false, pro: true },
                  { feature: 'Licita AI', basico: false, pro: true },
                ].map((row, index) => (
                  <motion.tr
                    key={row.feature}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.03 }}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-4 px-4 font-medium text-gray-700">{row.feature}</td>
                    <td className="py-4 px-4 text-center">
                      {typeof row.basico === 'boolean' ? (
                        row.basico ? (
                          <Check className="mx-auto text-green-500" size={20} />
                        ) : (
                          <span className="text-gray-300">—</span>
                        )
                      ) : (
                        <span className="font-medium text-gray-700">{row.basico}</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {typeof row.pro === 'boolean' ? (
                        row.pro ? (
                          <Check className="mx-auto text-purple-500" size={20} />
                        ) : (
                          <span className="text-gray-300">—</span>
                        )
                      ) : (
                        <span className="font-medium text-purple-600">{row.pro}</span>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-100 mb-4">
              <HelpCircle size={16} className="text-green-600" />
              <span className="text-sm font-medium text-green-600">Dúvidas frequentes</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Perguntas sobre os planos
            </h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl overflow-hidden shadow-sm"
              >
                <button
                  onClick={() => setExpandedFeature(expandedFeature === `faq-${index}` ? null : `faq-${index}`)}
                  className="w-full p-6 text-left flex items-center justify-between"
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <motion.div
                    animate={{ rotate: expandedFeature === `faq-${index}` ? 180 : 0 }}
                  >
                    <ChevronDown className="text-gray-400" />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {expandedFeature === `faq-${index}` && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <p className="px-6 pb-6 text-gray-600">{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
          }}
        />

        {/* Ondas decorativas */}
        <div className="absolute inset-0 pointer-events-none">
          <svg className="absolute top-0 left-0 right-0 w-full" viewBox="0 0 1440 100" preserveAspectRatio="none">
            <path
              d="M0,50 C360,100 720,0 1080,50 C1260,75 1380,25 1440,50 L1440,0 L0,0 Z"
              fill="rgba(255,255,255,0.1)"
              className="animate-wave"
            />
          </svg>
        </div>

        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-8"
            >
              <MessageCircle size={40} className="text-white" />
            </motion.div>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ainda tem dúvidas sobre qual plano escolher?
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
              Nossa equipe está pronta para ajudar você a escolher o plano perfeito para o seu negócio.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigateTo('https://app.meumei.net/register')}
                className="px-10 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, #d9f99d 0%, #bef264 100%)',
                  color: '#2C5E50',
                }}
              >
                Começar teste grátis
                <ArrowRight size={20} />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigateTo('/contato')}
                className="px-10 py-4 rounded-xl font-bold text-lg border-2 border-white text-white hover:bg-white/10 transition-colors"
              >
                Falar com especialista
              </motion.button>
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="text-white/60 mt-6 text-sm"
            >
              ✓ 7 dias grátis &nbsp;&nbsp; ✓ Sem cartão de crédito &nbsp;&nbsp; ✓ Cancele quando quiser
            </motion.p>
          </motion.div>
        </div>
      </section>

      <Footer />

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes spin-slow-reverse {
          from { transform: rotate(0deg); }
          to { transform: rotate(-360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes wave {
          0%, 100% { d: path("M0,50 C360,100 720,0 1080,50 C1260,75 1380,25 1440,50 L1440,0 L0,0 Z"); }
          50% { d: path("M0,30 C360,0 720,80 1080,30 C1260,10 1380,60 1440,30 L1440,0 L0,0 Z"); }
        }
        .animate-spin-slow {
          animation: spin-slow 50s linear infinite;
        }
        .animate-spin-slow-reverse {
          animation: spin-slow-reverse 60s linear infinite;
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-wave {
          animation: wave 8s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default PlanosPage;